import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import type { EffectParamSpec } from 'fym-shared/src/music24/musicVideoEffects';
import { TypedMemo } from '../../common/types';
import { effectParamMessages, effectValueMessages } from '../messages/effectMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface EffectParamControlProps {
  spec: EffectParamSpec;
  value: number | string | undefined;
  disabled: boolean;
  /** The grey text of an empty text field. */
  placeholder?: string;
  onChange: (value: number | string) => void;
}

/** The steps of a number slider: 100 from the minimum to the maximum. */
const SLIDER_STEPS = 100;

/**
 * One control of an effect param, drawn from its spec: a slider for a number, a select for an enum,
 * a text field for a text. A new param of the catalogue needs no UI code; a key or a value with no
 * message shows its own name.
 */
const EffectParamControlComponent: React.FC<EffectParamControlProps> = ({
  spec,
  value,
  disabled,
  placeholder,
  onChange,
}) => {
  const intl = useIntl();
  const id = useId();
  const labelMessage = effectParamMessages[spec.key];
  const label = labelMessage === undefined ? spec.key : intl.formatMessage(labelMessage);

  if (spec.type === 'number') {
    const current = typeof value === 'number' ? value : spec.default;
    return (
      <label className={styles.column} htmlFor={id}>
        <span className={styles.eyebrow}>{label}</span>
        <span className={styles.sliderRow}>
          <input
            id={id}
            type="range"
            aria-label={label}
            className={styles.range}
            min={spec.min}
            max={spec.max}
            step={(spec.max - spec.min) / SLIDER_STEPS}
            value={current}
            disabled={disabled}
            onChange={(event) => onChange(Number(event.target.value))}
          />
          <span className={styles.fieldUnit}>{current.toFixed(2)}</span>
        </span>
      </label>
    );
  }

  if (spec.type === 'enum') {
    const current = typeof value === 'string' && spec.values.includes(value) ? value : spec.default;
    return (
      <label className={styles.column} htmlFor={id}>
        <span className={styles.eyebrow}>{label}</span>
        <select
          id={id}
          className={styles.select}
          value={current}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          {spec.values.map((option) => {
            const optionMessage = effectValueMessages[option];
            return (
              <option key={option} value={option}>
                {optionMessage === undefined ? option : intl.formatMessage(optionMessage)}
              </option>
            );
          })}
        </select>
      </label>
    );
  }

  return (
    <label className={styles.column} htmlFor={id}>
      <span className={styles.eyebrow}>{label}</span>
      <input
        id={id}
        className={styles.textInput}
        value={typeof value === 'string' ? value : spec.default}
        maxLength={spec.maxLength}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
};

export const EffectParamControl = TypedMemo(EffectParamControlComponent);
